import {Item} from '@PosterWhiteboard/items/item/item.class';
import type {ShapeItemObject} from '@PosterWhiteboard/items/item/item.types';
import type {FillObject} from '@PosterWhiteboard/classes/fill.types';
import {Fill} from '@PosterWhiteboard/classes/fill.class';
import type {FillSurface} from '@PosterWhiteboard/classes/fill-surface';
import {withLayoutForSurface} from '@PosterWhiteboard/classes/fill-surface';
import type {DeepPartial} from '@/global';

/**
 * An item that paints its own interior with a fill: a rectangle (including a color background), a line or a vector. Every other item paints
 * nothing of its own, so it carries no fill.
 */
export abstract class ShapeItem extends Item {
  /** Solid black, as Fabric shapes have always painted until a fill is set. */
  public fill: Fill = Fill.createShapeDefault();

  public getFillObject(): FillObject {
    return this.fill.toObject();
  }

  /** How a fill switched to a gradient on this item picks its colors. */
  public abstract getFillSurface(): FillSurface;

  /** The fill another item's `fill` becomes when it moves onto this shape, by a pasted style or a replaced shape. */
  public getMovedFill(fill: FillObject): FillObject {
    return withLayoutForSurface(fill, this.getFillSurface());
  }

  public override copyVals(obj: DeepPartial<ShapeItemObject>): void {
    const {fill, ...plainObj} = obj;
    super.copyVals(plainObj);
    this.fill.copyVals(fill);
  }

  public override toObject(): ShapeItemObject {
    return {
      ...super.toObject(),
      fill: this.fill.toObject(),
    };
  }

  /**
   * A rectangle's or line's gradient is laid over its unscaled box and stretches with its scale, as their gradients always have. A resize handle
   * drag changes the scale without a redraw, so a scale change must not move the gradient either, or a reload would draw it differently.
   */
  protected doesChangeMoveTheBoxFill(obj: DeepPartial<ShapeItemObject>): boolean {
    return obj.fill !== undefined || obj.width !== undefined || obj.height !== undefined;
  }

  protected drawBoxFill(): void {
    this.fabricObject.set('fill', this.fill.getShapeFill({width: this.fabricObject.width, height: this.fabricObject.height}));
  }
}
